import { Config } from "@planetscale/database";
import { eq } from "drizzle-orm";
import { useState } from "react";
import { redirect, useNavigate } from "react-router";
import { requireSess } from "~/auth.server";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { trpc } from "~/TrpcClient";
import { GameVer, Lang, lstring, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, MetaArgs, OurLoaderArgs } from "~/util/Loaders";
import { homePage } from "~/util/titles";
import { Heading3, Heading4 } from "~/components/ui/Heading";
import { Button } from "~/components/ui/Button";
import css from "./PageForm.module.css";
import clsx from "clsx";
import { logPageView } from "~/util/stats";
import { z } from "zod";

const Params = z.object({ pageId: z.string() });

async function makeData(args: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(args);
  //
  const sess = requireSess(args.req.sess);
  if (!sess) {
    return redirect("/login");
  }
  const params = Params.parse(args.params);
  const page = await pg.db
    .select({
      id: pg.pages.id,
      title: pg.pages.title,
      content: pg.pages.content,
      lang: pg.pages.lang,
      game: pg.pages.game,
      objType: pg.pages.objType,
      objId: pg.pages.objId,
      user: { id: pg.users.id, login: pg.users.login },
    })
    .from(pg.pages)
    .where(eq(pg.pages.id, parseInt(params.pageId)))
    .innerJoin(pg.users, eq(pg.pages.userId, pg.users.id))
    .then((res) => res.at(0));

  if (!page) {
    return redirect("/pages");
  }

  if (page.user && sess.user.id !== page.user.id) {
    return redirect("/pages");
  }

  return page;
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string },
) {
  return {
    title: `${cx.t({
      en: "Edit Page",
      ru: "Редактировать Страницу",
    })} - ${homePage(cx.realm)}`,
  };
}

$PAGE({
  path: "pages/:pageId/edit",
  loader: $LOADER?.(async (args) => {
    return makeData(args, {});
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Create Page", ru: "Создать Страницу" },
      canonicalPath: `/pages/${cx.data?.id}`,
    }),
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const navigate = useNavigate();
    const pageTitle = t({ en: "Edit Page", ru: "Редактировать Страницу" });
    const [state, setState] = useState({
      title: data.title,
      content: data.content,
      lang: data.lang as Lang,
      game: data.game as GameVer[],
      objType: data.objType ?? undefined,
      objId: data.objId ?? undefined,
    });

    function handleChange(
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) {
      const { name, value } = e.target;
      setState((prevState) => ({ ...prevState, [name]: value }));
    }

    function handleCheckboxChange(value: GameVer) {
      setState((prev) => ({
        ...prev,
        game: prev.game.includes(value)
          ? prev.game.filter((g) => g !== value) // убираем, если уже есть
          : [...prev.game, value], // добавляем, если нет
      }));
    }

    const updatePage = trpc.pages.updatePage.useMutation();

    async function doUpdate() {
      const result = await updatePage.mutateAsync({
        data: state,
        pageId: data.id,
      });
      switch (result.t) {
        case "success": {
          navigate(`/pages/${data.id}`);
          break;
        }
      }
    }

    const disabled = () => !state.content || !state.title;

    return (
      <div>
        <div className="content">
          <Breadcrumb
            items={[
              {
                title: t({ en: "Pages", ru: "Страницы" }),
                url: turl("/pages"),
              },
              {
                title: t({ en: data.title, ru: data.title }),
                url: turl(`/pages/${data.id}`),
              },
              { title: pageTitle, url: turl(`/pages/${data.id}/edit`) },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>

          <form
            className={css.root}
            onSubmit={(e) => {
              if (disabled()) {
                return;
              }

              e.preventDefault();
              doUpdate();
            }}
            autoComplete="off"
          >
            <Heading3>{t({ en: "Options", ru: "Опции" })}</Heading3>
            <div className="columns">
              <div className={clsx("column", css.wrap)}>
                <Heading4>{t({ en: "Language", ru: "Язык" })}</Heading4>
                <div className={css.desc}>
                  {t({
                    en: "Select the language in which the page will be displayed",
                    ru: "Выберите язык на котором будет отображаться страница",
                  })}
                </div>
                <select
                  className={css.select}
                  id="lang"
                  name="lang"
                  value={state.lang}
                  onChange={handleChange}
                >
                  {Lang.options.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <div className={clsx("column", css.wrap)}>
                <Heading4>
                  {t({ en: "Game Version", ru: "Версия игры" })}
                </Heading4>
                <div className={css.desc}>
                  {t({
                    en: "Select which chronicles this page will be displayed on, if nothing is selected, then the page will be displayed for all chronicles.",
                    ru: "Выберите на каких хрониках будет отображаться эта страница, если ничего не выбрано, то страница будет отображаться для всех хроник.",
                  })}
                </div>
                <div className={css.checkboxBlock}>
                  {GameVer.options.map((ver) => (
                    <label key={ver} className={css.checkbox}>
                      <input
                        type="checkbox"
                        value={ver}
                        checked={state.game.includes(ver)}
                        onChange={() => handleCheckboxChange(ver)}
                      />
                      {ver}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="columns">
              <div className={clsx("column", css.wrap)}>
                <Heading4>
                  {t({ en: "Object Type", ru: "Тип объекта" })}
                </Heading4>
                <div className={css.desc}>
                  {t({
                    en: 'Select the type of object that the page will be linked to. For example, "Quest".',
                    ru: 'Выберите тип объекта к которому будет привязана страница. Например "Квест".',
                  })}
                </div>
                <input
                  className={css.input}
                  type="text"
                  name="objType"
                  value={state.objType}
                  onChange={handleChange}
                />
              </div>
              <div className={clsx("column", css.wrap)}>
                <Heading4>{t({ en: "Object ID", ru: "ID Объекта" })}</Heading4>
                <div className={css.desc}>
                  {t({
                    en: "Select the type of object ID that the page will be linked to.",
                    ru: "Выберите тип ID объекта к которому будет привязана страница.",
                  })}
                </div>
                <input
                  className={css.input}
                  type="text"
                  name="objId"
                  value={state.objId}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Heading3>{t({ en: "Heading", ru: "Заголовок" })}</Heading3>
            <input
              className={css.input}
              type="text"
              name="title"
              value={state.title}
              onChange={handleChange}
            />

            <Heading3>{t({ en: "Content", ru: "Контент" })}</Heading3>
            <textarea
              className={css.textarea}
              name="content"
              value={state.content}
              onChange={handleChange}
            />
            <div style={{ marginTop: 20 }}>
              <Button type="submit" disabled={disabled()}>
                {t({ en: "Save", ru: "Сохранить" })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  },
});
